import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, DialogActions, DialogContent, DialogTitle, FormControl, FormLabel, LinearProgress, Tooltip, Typography } from "@mui/material";
import SyncRounded from "@mui/icons-material/SyncRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import { MonitorButton, MonitorDialog, MonitorSelect } from "./MonitorControls";
import { api } from "../utils/helpers";
import "./PartnerDirectoryEdgeSync.css";

// Limit concurrent transport-list status reads; the normal PD page keeps its existing path.
let devReads = 0;
const devReadQueue = [];
async function devRead(url, options, current) {
  if (devReads >= 4) await new Promise(resolve => devReadQueue.push(resolve));
  else devReads += 1;
  try { return current() ? await api(url, options) : null; }
  finally { const next = devReadQueue.shift(); if (next) next(); else devReads -= 1; }
}

const activeStates = new Set(["dispatching", "queued", "running", "unknown"]);
const setupVariants = {
  EDGE_WORKFLOW_SETUP_REQUIRED: { title: "setupTitle", description: "setupDescription", action: "configure" },
  EDGE_WORKFLOW_INCOMPLETE: { title: "completeTitle", description: "completeDescription", action: "complete" },
  EDGE_WORKFLOW_UPDATE_REQUIRED: { title: "updateTitle", description: "updateDescription", action: "update" },
};

// The parent keys this component by instance and PID. Responses never migrate to another PID.
export default function PartnerDirectoryEdgeSync({ instanceId, instanceName, pid, blocked = false, blockedReason = "saveFirst", transportDev, sharedCapabilities, compact = false, pidLabel, deferUntilOpen = false }) {
  const { t } = useTranslation("partnerdirectory");
  const id = useId();
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [run, setRun] = useState(null);
  const [open, setOpen] = useState(false);
  const [requested, setRequested] = useState(false);
  const deferred = deferUntilOpen && !requested;
  const [working, setWorking] = useState(false);
  const [updateRequested, setUpdateRequested] = useState(false);
  const [target, setTarget] = useState("");
  const [success, setSuccess] = useState(false);
  const alive = useRef(false);
  const startLock = useRef(null);
  const operationVersion = useRef(0);
  const uncertainPreviousId = useRef(null);
  const loadVersion = useRef(0);
  const base = transportDev
    ? `/api/cicd/transport/${encodeURIComponent(transportDev.spaceId)}/${encodeURIComponent(instanceId)}/partner-directory/dev-edge-sync`
    : `/api/${encodeURIComponent(instanceId)}/partner-directory/edge-sync`;
  const scope = `${base}:${pid}`;
  const suppliedConfig = sharedCapabilities?.value;
  const suppliedLoading = sharedCapabilities?.loading;
  const suppliedIdle = sharedCapabilities?.idle;
  const suppliedError = sharedCapabilities?.error;
  const suppliedReload = sharedCapabilities?.reload;
  const usesShared = !!sharedCapabilities;
  const isDev = !!transportDev;
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const reasonText = code => t(`edgeSync.reasons.${code}`, { defaultValue: t("edgeSync.readFailed") });
  const load = useCallback(async () => {
    const version = ++loadVersion.current;
    const operation = operationVersion.current;
    const current = () => alive.current && currentScope.current === scope && version === loadVersion.current;
    if (deferred) { setLoading(false); setError(null); return; }
    setLoading(true); setError(null);
    if (usesShared && (suppliedLoading || suppliedIdle)) return;
    if (usesShared && suppliedError) { setError(suppliedError); setLoading(false); return; }
    try {
      const config = usesShared ? suppliedConfig : await api(`${base}/capabilities`, { cache: "no-store" });
      if (!config || typeof config.available !== "boolean" || !Array.isArray(config.targets)) throw new Error("EDGE_REQUEST_FAILED");
      if (!current()) return;
      const latest = config.reason === "EDGE_PIPELINE_MISSING" ? null
        : await (isDev ? devRead(`${base}/runs?pid=${encodeURIComponent(pid)}`, { cache: "no-store" }, current) : api(`${base}/runs?pid=${encodeURIComponent(pid)}`));
      if (!current()) return;
      setCapabilities(config);
      setRun(previous => {
        if (operation !== operationVersion.current || startLock.current === scope) return previous;
        if (latest?.id && latest.id !== uncertainPreviousId.current) return latest;
        if (activeStates.has(previous?.state) || startLock.current === scope) return previous;
        return latest;
      });
      setTarget(previous => config.targets?.some(item => item.id === previous) ? previous
        : config.targets?.length === 1 ? config.targets[0].id : "");
    } catch (e) { if (current()) setError(e.code || "EDGE_REQUEST_FAILED"); }
    finally { if (current()) setLoading(false); }
  }, [base, pid, scope, isDev, usesShared, suppliedLoading, suppliedError, suppliedConfig, suppliedIdle, deferred]);

  useEffect(() => {
    alive.current = true;
    startLock.current = null; uncertainPreviousId.current = null;
    setRequested(false); setCapabilities(null); setRun(null); setTarget(""); setOpen(false); setSuccess(false); setWorking(false); setUpdateRequested(false);
    return () => { alive.current = false; loadVersion.current += 1; };
  }, [scope]);
  useEffect(() => { load(); }, [load]);

  const active = activeStates.has(run?.state);
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false, timer;
    const poll = async () => {
      try {
        const url = run?.id ? `${base}/runs/${encodeURIComponent(run.id)}${isDev ? `?pid=${encodeURIComponent(pid)}` : ""}`
          : `${base}/runs?pid=${encodeURIComponent(pid)}`;
        const next = await (isDev ? devRead(url, { cache: "no-store" }, () => !cancelled) : api(url));
        if (cancelled) return;
        if (!next || next.id === uncertainPreviousId.current || typeof next.state !== "string") { timer = setTimeout(poll, 5000); return; }
        setRun(next); setError(null);
        if (next.state === "success") setSuccess(true);
        if (activeStates.has(next.state)) timer = setTimeout(poll, 5000);
      } catch (e) {
        if (!cancelled) { setError(e.code || "EDGE_RUN_UNREADABLE"); timer = setTimeout(poll, 10000); }
      }
    };
    timer = setTimeout(poll, 2000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [active, base, run?.id, isDev, pid]);

  useEffect(() => {
    if (!success || compact) return undefined;
    const timer = setTimeout(() => setSuccess(false), 2600);
    return () => clearTimeout(timer);
  }, [success, compact]);

  const optionalUpdate = !isDev && capabilities?.available === true && capabilities?.updateAvailable === true && capabilities?.canConfigure === true;
  const setup = setupVariants[capabilities?.reason] || (updateRequested && optionalUpdate ? setupVariants.EDGE_WORKFLOW_UPDATE_REQUIRED : null);
  const setupPossible = !isDev && !!setup && capabilities.canConfigure;
  const busy = working || active;
  const disabled = blocked || busy || success || (!deferred && (loading || (!error && !capabilities?.available && !setupPossible)));
  const explanation = capabilities?.reason === "EDGE_PIPELINE_MISSING" ? reasonText(capabilities.reason)
    : blocked ? t(`edgeSync.${blockedReason}`) : deferred ? t("edgeSync.hint") : loading ? t("edgeSync.checking")
    : active ? t(`edgeSync.states.${run.state}`) : error ? reasonText(error)
    : capabilities?.reason ? reasonText(capabilities.reason) : t("edgeSync.hint");

  const refresh = () => usesShared ? suppliedReload() : load();
  const openSync = () => {
    if (deferred) { setRequested(true); sharedCapabilities?.request?.(); }
    if (error && !deferred) refresh();
    else { setUpdateRequested(false); setOpen(true); }
  };

  async function configure() {
    if (busy || blocked || loading || !setupPossible) return;
    setWorking(true); setError(null);
    try {
      await api(`${base}/configure`, { method: "POST", body: "{}", timeout: 90000 });
      if (alive.current && currentScope.current === scope) { await load(); setUpdateRequested(false); }
    } catch (e) { if (alive.current && currentScope.current === scope) setError(e.code || "EDGE_REQUEST_FAILED"); }
    finally { if (alive.current && currentScope.current === scope) setWorking(false); }
  }

  async function start() {
    if (!target || blocked || busy || startLock.current === scope || !capabilities?.available) return;
    startLock.current = scope; operationVersion.current += 1;
    const previousId = run?.id;
    setWorking(true); setError(null);
    try {
      const next = await api(`${base}/runs`, { method: "POST", body: JSON.stringify({ pid, runtimeId: target }), timeout: 90000 });
      if (alive.current && currentScope.current === scope) { operationVersion.current += 1; setRun(next); setOpen(false); }
    } catch (e) {
      if (alive.current && currentScope.current === scope) {
        // A lost HTTP response can still mean a dispatched run. Recover the durable record.
        setError(e.code || "EDGE_DISPATCH_UNCERTAIN");
        operationVersion.current += 1;
        const uncertain = !e.status || e.status >= 500 || e.code === "EDGE_DISPATCH_UNCERTAIN";
        if (uncertain) { uncertainPreviousId.current = previousId || null; setRun({ state: "unknown" }); setOpen(false); }
        try {
          const latest = await api(`${base}/runs?pid=${encodeURIComponent(pid)}`);
          if (alive.current && currentScope.current === scope && latest && latest.id !== previousId && (activeStates.has(latest.state) || (uncertain && ["success", "failed"].includes(latest.state)))) { setRun(latest); setOpen(false); }
        } catch { /* Keep the explicit unknown state; never submit a second POST automatically. */ }
      }
    } finally { if (alive.current && currentScope.current === scope) { startLock.current = null; setWorking(false); } }
  }

  // Compact result state: use run.state directly so results persist after the success animation flag clears.
  const compactState = active ? "running" : success || run?.state === "success" ? "success"
    : (error || run?.state === "failed") ? "failed" : loading ? "loading" : null;
  const compactReasonCode = error || run?.code || "EDGE_SYNC_FAILED";
  const compactFailurePhase = !error && ["BEFORE_TARGET_WRITE", "TARGET_WRITE_POSSIBLE"].includes(run?.failurePhase) ? run.failurePhase : null;
  const runUrl = run?.url && (active || run?.state === "failed" || run?.state === "success") ? run.url : null;

  const innerDialog = open && <MonitorDialog className="pd-edge-sync-dialog" maxWidth="sm" aria-labelledby={`${id}-title`}
    onClose={() => !working && setOpen(false)}>
    <DialogTitle id={`${id}-title`}>{t(setupPossible ? `edgeSync.${setup.title}` : "edgeSync.title")}</DialogTitle>
    <DialogContent>
      {loading && <LinearProgress aria-label={t("edgeSync.checking")} sx={{ mb: 2 }} />}
      <p className="pd-edge-sync-intro">{t(setupPossible ? `edgeSync.${setup.description}` : "edgeSync.description")}</p>
      {setupPossible && <p>{t("edgeSync.setupScope")}</p>}
      <dl className="pd-edge-sync-summary">
        {setupPossible ? <div><dt>{t("edgeSync.space")}</dt><dd>{t("edgeSync.wholeSpace")}</dd></div> : <>
        <div><dt>{t("edgeSync.source")}</dt><dd>{instanceName}</dd></div>
        <div><dt>{t("edgeSync.partner")}</dt><dd>{pid}</dd></div>
        {capabilities?.runtimeSource && <div><dt>{t("edgeSync.runtimeSource")}</dt><dd>{capabilities.runtimeSource.name}</dd></div>}
        {capabilities?.syncUser && <div><dt>{t("edgeSync.syncUser")}</dt><dd>{capabilities.syncUser}</dd></div>}
        </>}
      </dl>
      {setupPossible ? <div className="pd-edge-sync-prerequisites">
        <p>{t("edgeSync.requirementsTitle")}</p>
        <ul><li>{t("edgeSync.requirementApi")}</li><li>{t("edgeSync.requirementCredentials")}</li><li>{t("edgeSync.requirementRunner")}</li></ul>
      </div> : <FormControl fullWidth className="pd-edge-sync-target">
        <FormLabel id={`${id}-target`}>{t("edgeSync.target")}</FormLabel>
        <MonitorSelect aria-labelledby={`${id}-target`} aria-label={t("edgeSync.target")} value={target}
          disabled={working} onChange={event => setTarget(event.target.value)}>
          <option value="" disabled>{t("edgeSync.chooseTarget")}</option>
          {(capabilities?.targets || []).map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
        </MonitorSelect>
      </FormControl>}
      {!setupPossible && <p className="pd-edge-sync-note">{t("edgeSync.mergeNote")}</p>}
      {!setupPossible && capabilities?.updateAvailable === true && <p className="pd-edge-sync-note">
        {t(isDev || !optionalUpdate ? "edgeSync.updateAvailableManaged" : "edgeSync.updateAvailable")}
      </p>}
      {error && <Alert severity="error">{reasonText(error)}</Alert>}
    </DialogContent>
    <DialogActions>
      <MonitorButton onClick={() => setOpen(false)} disabled={working}>{t("edgeSync.cancel")}</MonitorButton>
      {optionalUpdate && !setupPossible && <MonitorButton variant="outlined" disabled={busy || blocked || loading}
        onClick={() => setUpdateRequested(true)}>{t("edgeSync.update")}</MonitorButton>}
      <MonitorButton variant="contained" onClick={setupPossible ? configure : start}
        disabled={busy || blocked || loading || (!setupPossible && (!target || !capabilities?.available))} startIcon={<SyncRounded className={working ? "pd-edge-sync-spin" : ""}/> }>
        {t(working ? "edgeSync.working" : setupPossible ? `edgeSync.${setup.action}` : "edgeSync.start")}
      </MonitorButton>
    </DialogActions>
  </MonitorDialog>;

  if (compact) return <Box className="pd-edge-sync-compact" sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
    <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
      {pidLabel && <Typography variant="body2" sx={{ color: "var(--iio-text-1)", fontWeight: 500, wordBreak: "break-word" }}>{pidLabel}</Typography>}
      <Box role="status" aria-live="polite" sx={{ mt: pidLabel ? 0.5 : 0 }}>
        {compactState === "loading" && <Typography variant="body2" sx={{ color: "var(--iio-text-3)", fontSize: 12 }}>{t("edgeSync.checking")}</Typography>}
        {compactState === "running" && <>
          <Typography component="span" variant="body2" sx={{ color: "var(--iio-brand-fg)", fontWeight: 600, fontSize: 12 }}>
            {t("edgeSync.compactRunning")}
          </Typography>
          {runUrl && <a className="pd-edge-sync-link pd-edge-sync-link-inline" href={runUrl} target="_blank" rel="noreferrer">
            {t("edgeSync.viewRun")} <OpenInNewRounded sx={{ fontSize: 13, verticalAlign: "text-bottom" }}/>
          </a>}
        </>}
        {compactState === "success" && <Typography component="span" variant="body2" sx={{ color: "var(--iio-success)", fontWeight: 600, fontSize: 12 }}>
          {t("edgeSync.compactSuccess")}
        </Typography>}
        {compactState === "failed" && <>
          <Typography component="span" variant="body2" sx={{ color: "var(--iio-danger)", fontWeight: 600, fontSize: 12 }}>
            {t("edgeSync.compactFailed")}
          </Typography>
          <Typography variant="body2" sx={{ color: "var(--iio-text-2)", fontSize: 12, mt: 0.25 }}>
            {reasonText(compactReasonCode)}
          </Typography>
          {compactFailurePhase && <Typography variant="body2" sx={{ color: "var(--iio-text-3)", fontSize: 12 }}>
            {t(`edgeSync.failurePhases.${compactFailurePhase}`)}
          </Typography>}
          {runUrl && <a className="pd-edge-sync-link pd-edge-sync-link-inline" href={runUrl} target="_blank" rel="noreferrer">
            {t("edgeSync.viewRun")} <OpenInNewRounded sx={{ fontSize: 13, verticalAlign: "text-bottom" }}/>
          </a>}
        </>}
      </Box>
    </Box>
    <MonitorButton variant="outlined" size="small" disabled={disabled} aria-busy={busy}
      onClick={openSync}
      startIcon={compactState === "success" ? <CheckRounded/> : <SyncRounded className={busy ? "pd-edge-sync-spin" : ""}/>}
      sx={{ flexShrink: 0, whiteSpace: "nowrap" }}>
      {compactState === "success" ? t("edgeSync.compactSuccess") : error && !active ? t("edgeSync.retry") : t("edgeSync.compactButton")}
    </MonitorButton>
    {innerDialog}
  </Box>;

  return <div className={`pd-edge-sync${isDev ? " pd-edge-sync-dev" : ""}`}>
    <Tooltip title={explanation} describeChild arrow>
      <span className="pd-edge-sync-anchor" tabIndex={disabled ? 0 : undefined} aria-label={disabled ? `${t(isDev ? "edgeSync.devButton" : "edgeSync.button")}: ${explanation}` : undefined}>
        <MonitorButton variant="outlined" className={`pd-edge-sync-button${busy ? " is-running" : ""}${success ? " is-success" : ""}`}
          disabled={disabled} aria-busy={busy} aria-describedby={`${id}-status`}
          onClick={openSync}
          startIcon={success ? <CheckRounded/> : <SyncRounded className={busy ? "pd-edge-sync-spin" : ""}/>}>
          {success ? t("edgeSync.done") : error && !active ? t("edgeSync.retry") : t(isDev ? "edgeSync.devButton" : "edgeSync.button")}
        </MonitorButton>
      </span>
    </Tooltip>
    {isDev && !loading && !busy && !error && capabilities?.reason && <div className="pd-edge-sync-status">{reasonText(capabilities.reason)}</div>}
    {!loading && !busy && !error && capabilities && !capabilities.available && <MonitorButton variant="text"
      onClick={refresh} disabled={blocked}>{t("edgeSync.retry")}</MonitorButton>}
    <div id={`${id}-status`} className="pd-edge-sync-status" role="status" aria-live="polite">
      {busy && !error && t(`edgeSync.states.${run?.state || "dispatching"}`)}
      {success && t("edgeSync.verified")}
    </div>
    {(error || run?.state === "failed") && <Alert severity="error" className="pd-edge-sync-error">
      {reasonText(error || run.code || "EDGE_SYNC_FAILED")}
      {!error && ["BEFORE_TARGET_WRITE", "TARGET_WRITE_POSSIBLE"].includes(run?.failurePhase)
        && <p>{t(`edgeSync.failurePhases.${run.failurePhase}`)}</p>}
    </Alert>}
    {run?.url && (active || run.state === "failed") && <a className="pd-edge-sync-link" href={run.url} target="_blank" rel="noreferrer">
      {t("edgeSync.viewRun")} <OpenInNewRounded fontSize="inherit"/>
    </a>}
    {innerDialog}
  </div>;
}
